/**
 * What this VoiceAgent's conversations cost (plan-017 WAPP-B, design §29.11 row 12, owner answer OQ-1: "an agent's
 * page the rates of its connections"). The api answers the organization's rates of the kinds the VoiceAgent's enabled
 * connections are charged as (`VoiceAgentDetail.talk_rates`): a WeTalk number `carrier_leg_inbound` (and
 * `carrier_leg_outbound` when it places or forwards calls), a number on the organization's own SIP trunk
 * `sip_trunk_inbound` (and `sip_trunk_outbound`), a website widget `text` (and `browser` with voice), the portal's
 * browser test path `browser`. Each is drawn with the glossary's customer copy and its rate a minute — never a literal —
 * then "Tests are free." (owner answers Q4, PQ-14). Nothing routes to it yet: it says so and names no rate.
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { useGateway } from "../../../../api/provider.js";
import { voiceAgentQuery } from "../../../../api/query.js";
import { talkRateKindLabel, talkRateText, type TalkRateView } from "../../../../api/slice/talk-rate.js";

/** One line per kind: "Calls to your WeTalk number" and "<rate> a minute". */
export function connectionRateLinesOf(rates: readonly TalkRateView[]): readonly { readonly label: string; readonly rate: string }[] {
  return rates.map((one) => ({ label: talkRateKindLabel(one.kind), rate: talkRateText(one.perMinute, "minute") }));
}

export function ConnectionRates({ voiceAgentId }: { readonly voiceAgentId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const voiceAgent = useQuery(voiceAgentQuery(gateway, voiceAgentId));
  const rates = voiceAgent.data?.detail?.talkRates;
  if (rates === undefined) return null;
  const line = connectionRateLinesOf(rates);
  return (
    <section aria-label={portalText("operations", "connection_rates.title")}>
      <p className="ch-section-title">{portalText("operations", "connection_rates.title")}</p>
      {line.length === 0 ? (
        <p className="ch-section-dek">{portalText("operations", "connection_rates.none")}</p>
      ) : (
        <dl className="ch-rate-list">
          {line.map((one) => (
            <div className="ch-rate" key={one.label}>
              <dt>{one.label}</dt>
              <dd>{one.rate}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="ch-section-dek">{portalText("core", "talk_rate.tests_are_free")}</p>
    </section>
  );
}
